import { useState } from "react";
import { createAdminMenuItems } from "@x-veloper/dataslot-auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCog,
  faHome,
  faPercentage,
  faChartBar,
  faUsers,
  faUserCog,
  faCubes,
  faBars,
  faIndustry,
} from "@fortawesome/free-solid-svg-icons";
import { PreviewModeBanner } from "./PreviewModeBanner";
import { SalespersonPreviewBanner } from "./SalespersonPreviewBanner";
import Sidebar, { type MenuItem } from "./layout/Sidebar";
import { t } from "@/shared/lib/th-translations";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Admin menu items with FontAwesome icon
  const adminMenuItems = createAdminMenuItems({
    userManagementIcon: <FontAwesomeIcon icon={faUserCog} />,
  });

  // Menu structure for commission application
  const menuItems: MenuItem[] = [
    {
      title: t('navigation.commission'),
      icon: faChartLine,
      items: [
        {
          title: t('navigation.dashboard'),
          url: "/",
          icon: faHome,
        },
        {
          title: t('navigation.commissionRates'),
          url: "/admin/commission-rates",
          icon: faPercentage,
          adminOnly: true,
        },
      ],
    },
    {
      title: t('navigation.salesAnalytics'),
      icon: faChartBar,
      items: [
        {
          title: t('navigation.overview'),
          url: "/analytics/overview",
          icon: faChartBar,
        },
        {
          title: t('navigation.customerPortfolio'),
          url: "/analytics/customers",
          icon: faUsers,
        },
        {
          title: t('navigation.rfmAnalysis'),
          url: "/analytics/rfm",
          icon: faCubes,
        },
      ],
    },
    {
      title: t('navigation.production'),
      icon: faIndustry,
      adminOnly: true,
      items: [
        {
          title: t('navigation.productionOverview'),
          url: "/production/overview",
          icon: faIndustry,
        },
        {
          title: t('navigation.productionPerformance'),
          url: "/production/performance",
          icon: faChartBar,
        },
        {
          title: t('navigation.productionMachines'),
          url: "/production/machines",
          icon: faIndustry,
        },
        {
          title: t('navigation.productionPeople'),
          url: "/production/people",
          icon: faUsers,
        },
      ],
    },
    {
      title: t('navigation.settings'),
      icon: faCog,
      url: "/settings",
    },
  ];

  return (
    <div className="flex flex-col h-full overflow-auto">
      <PreviewModeBanner />
      <SalespersonPreviewBanner />
      <div className="flex flex-1 overflow-hidden min-w-0">
        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          menuItems={[...menuItems, ...adminMenuItems]}
        />

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Page Content */}
          <main className="flex-1 overflow-auto bg-gray-50 p-4 lg:p-6">
            {children}
          </main>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed bottom-6 right-6 z-30 lg:hidden bg-blue-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Open menu"
        >
          <FontAwesomeIcon icon={faBars} className="text-xl" />
        </button>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
